import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { cn } from '../../../utils/cn';

const SearchResults = ({ results = [], isLoading, onConnectionRequest, currentUser }) => {
  
  // Loading skeleton
  const LoadingSkeleton = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {[...Array(6)]?.map((_, index) => (
        <div key={`skeleton-${index}`} className="bg-card rounded-lg border border-border p-6 shadow-sm animate-pulse">
          <div className="flex items-center space-x-4 mb-4">
            <div className="h-12 w-12 bg-muted rounded-full"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-muted rounded w-3/4"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </div>
          </div>
          <div className="space-y-3 mb-4">
            <div className="h-3 bg-muted rounded"></div>
            <div className="h-3 bg-muted rounded w-5/6"></div>
            <div className="h-3 bg-muted rounded w-4/6"></div>
          </div>
          <div className="flex space-x-2 mb-4">
            <div className="h-6 w-16 bg-muted rounded-full"></div>
            <div className="h-6 w-20 bg-muted rounded-full"></div>
            <div className="h-6 w-14 bg-muted rounded-full"></div>
          </div>
          <div className="h-10 bg-muted rounded"></div>
        </div>
      ))}
    </div>
  );

  // Empty results
  const EmptyResults = () => (
    <div className="text-center py-12">
      <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
      <h3 className="text-lg font-semibold text-card-foreground mb-2">
        Aucun résultat trouvé
      </h3>
      <p className="text-muted-foreground mb-4">
        Essayez de modifier vos critères de recherche ou vos filtres.
      </p>
      <div className="flex flex-wrap justify-center gap-2">
        <Button variant="outline" size="sm">
          Effacer les filtres
        </Button>
        <Button variant="outline" size="sm">
          Recherche élargie
        </Button>
      </div>
    </div>
  );

  // Format location
  const formatLocation = (partner) => {
    const parts = [partner?.city, partner?.country]?.filter(Boolean);
    return parts?.join(', ') || 'Non spécifiée';
  };

  // Get partner type display
  const getPartnerTypeDisplay = (type) => {
    const types = {
      agent: { label: 'Agent', icon: 'Briefcase', color: 'text-blue-600' },
      venue: { label: 'Venue', icon: 'MapPin', color: 'text-green-600' },
      booker: { label: 'Booker', icon: 'Calendar', color: 'text-purple-600' },
      independent: { label: 'Indépendant', icon: 'User', color: 'text-orange-600' }
    };
    return types?.[type] || { label: type, icon: 'User', color: 'text-gray-600' };
  };

  // Get profession display
  const getProfessionDisplay = (profession) => {
    const professions = {
      musician: { label: 'Musicien', icon: 'Music' },
      singer: { label: 'Chanteur', icon: 'Mic' },
      dj: { label: 'DJ', icon: 'Disc3' },
      producer: { label: 'Producteur', icon: 'Headphones' },
      model: { label: 'Modèle', icon: 'Camera' },
      actor: { label: 'Acteur', icon: 'Film' },
      magician: { label: 'Magicien', icon: 'Sparkles' },
      dancer: { label: 'Danseur', icon: 'Users' },
      performer: { label: 'Performeur', icon: 'Star' },
      other: { label: 'Autre', icon: 'MoreHorizontal' }
    };
    return professions?.[profession] || { label: profession, icon: 'User' };
  };

  // Render rating stars
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars?.push(<Icon key={i} name="Star" size={14} className="text-yellow-500 fill-current" />);
      } else if (i === fullStars && hasHalfStar) {
        stars?.push(<Icon key={i} name="Star" size={14} className="text-yellow-500 fill-current opacity-50" />);
      } else {
        stars?.push(<Icon key={i} name="Star" size={14} className="text-gray-300" />);
      }
    }
    return stars;
  };

  // Get availability status display
  const getAvailabilityStatus = (status) => {
    const statuses = {
      available: { label: 'Disponible', color: 'text-success', bgColor: 'bg-success/10' },
      busy: { label: 'Occupé', color: 'text-warning', bgColor: 'bg-warning/10' },
      unavailable: { label: 'Indisponible', color: 'text-error', bgColor: 'bg-error/10' }
    };
    return statuses?.[status] || { label: status, color: 'text-muted-foreground', bgColor: 'bg-muted' };
  };

  // Check if user can send connection request
  const canSendConnectionRequest = (partner) => {
    return currentUser && currentUser?.id !== partner?.user_profiles?.id;
  };

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (!results || results?.length === 0) {
    return <EmptyResults />;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {results?.map((partner, index) => {
        const partnerTypeInfo = getPartnerTypeDisplay(partner?.partner_type);
        const professionInfo = partner?.artist_profession ? getProfessionDisplay(partner?.artist_profession) : null;
        const availabilityInfo = getAvailabilityStatus(partner?.availability_status);
        const userProfile = partner?.user_profiles;
        const musicalSpecialties = partner?.partner_musical_specialties || [];
        const professionData = partner?.partner_profession_data?.[0];

        return (
          <div
            key={`partner-${partner?.id || index}`}
            className="bg-card rounded-lg border border-border p-6 shadow-sm hover:shadow-md transition-shadow"
          >
            {/* Header */}
            <div className="flex items-start space-x-4 mb-4">
              <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                {userProfile?.avatar_url ? (
                  <img
                    src={userProfile?.avatar_url}
                    alt={userProfile?.full_name}
                    className="h-12 w-12 rounded-full object-cover"
                  />
                ) : (
                  <Icon name="User" size={24} className="text-muted-foreground" />
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <h3 className="text-lg font-semibold text-card-foreground truncate">
                    {partner?.business_name || userProfile?.full_name}
                  </h3>
                  {partner?.is_verified && (
                    <Icon name="Shield" size={16} className="text-success" />
                  )}
                </div>
                
                <div className="flex items-center space-x-3 mb-2">
                  <div className="flex items-center space-x-1">
                    <Icon name={partnerTypeInfo?.icon} size={14} className={partnerTypeInfo?.color} />
                    <span className="text-sm text-muted-foreground">
                      {partnerTypeInfo?.label}
                    </span>
                  </div>
                  
                  {professionInfo && (
                    <>
                      <span className="text-xs text-muted-foreground">•</span>
                      <div className="flex items-center space-x-1">
                        <Icon name={professionInfo?.icon} size={14} className="text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          {professionInfo?.label}
                        </span>
                      </div>
                    </>
                  )}
                </div>
                
                <div className="flex items-center space-x-2">
                  <Icon name="MapPin" size={14} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {formatLocation(partner)}
                  </span>
                </div>
              </div>
            </div>

            {/* Description */}
            {partner?.description && (
              <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                {partner?.description}
              </p>
            )}

            {/* Musical Specialties */}
            {musicalSpecialties?.length > 0 && (
              <div className="mb-4">
                <div className="flex flex-wrap gap-1">
                  {musicalSpecialties?.slice(0, 3)?.map((specialty, specIndex) => (
                    <span
                      key={`specialty-${specialty?.specialty || specIndex}`}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent/20 text-accent-foreground"
                    >
                      {specialty?.specialty}
                    </span>
                  ))}
                  {musicalSpecialties?.length > 3 && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted/50 text-muted-foreground">
                      +{musicalSpecialties?.length - 3}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center">
                <div className="flex items-center justify-center space-x-1 mb-1">
                  {renderStars(partner?.average_rating || 0)}
                </div>
                <p className="text-sm text-muted-foreground">
                  {partner?.average_rating?.toFixed(1) || '0.0'} étoiles
                </p>
              </div>
              
              <div className="text-center">
                <p className="text-lg font-bold text-card-foreground">
                  {partner?.total_bookings || 0}
                </p>
                <p className="text-sm text-muted-foreground">événements</p>
              </div>
            </div>

            {/* Availability */}
            <div className="mb-4">
              <div className={cn(
                "inline-flex items-center px-3 py-1 rounded-full text-sm font-medium",
                availabilityInfo?.bgColor,
                availabilityInfo?.color
              )}>
                <Icon name="Clock" size={14} className="mr-1" />
                {availabilityInfo?.label}
              </div>
            </div>

            {/* Experience */}
            {partner?.years_experience > 0 && (
              <div className="mb-4">
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="Trophy" size={14} />
                  <span>{partner?.years_experience} ans d'expérience</span>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col space-y-2">
              {canSendConnectionRequest(partner) && (
                <Button
                  variant="default"
                  size="sm"
                  iconName="MessageCircle"
                  iconPosition="left"
                  fullWidth
                  onClick={() => onConnectionRequest?.(partner)}
                >
                  Demander une connexion
                </Button>
              )}
              
              <Button
                variant="outline"
                size="sm"
                iconName="Eye"
                iconPosition="left"
                fullWidth
                onClick={() => {
                  // Navigate to partner profile
                  window.open(`/partner-profile?id=${partner?.id}`, '_blank');
                }}
              >
                Voir le profil
              </Button>
            </div>

            {/* Premium Badge */}
            {partner?.is_verified && (
              <div className="absolute top-2 right-2">
                <div className="bg-success text-success-foreground px-2 py-1 rounded-full text-xs font-medium">
                  Vérifié
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SearchResults;